import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import {
  AcesoResponse,
  AcesoTvControlRequest,
  InteractiveSessionInfo,
  InteractiveSessionJoinRequestResponse,
  InteractiveSessionRegistrant,
  InteractiveSessionRequestResponse,
  RegisteredUser
} from "@vp/models";
import { API_BASE_URL } from "@vp/shared/tokens";
import { createUrlBuilder } from "@vp/shared/utilities";
import { Operation } from "rfc6902";
import { Observable, Subject, Subscription } from "rxjs";

export interface InteractiveChannelParameters {
  callType: string;
  deviceId: string | null;
  caseId: string | null;
  requestCall: boolean;
  visitTagId?: string | null;
  visitTagDisplayName?: string | null;
}

export interface TranslationServiceParams {
  facilityId: string | undefined;
  pin: string | undefined;
  topic: string | undefined;
  applicationHostId: string | undefined;
  languageCode: string | undefined;
  userId: string | undefined;
}

@Injectable({
  providedIn: "root"
})
export class VirtualCareApiService implements OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private _destroyed = new Subject<void>();

  constructor(
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    private readonly _http: HttpClient
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this._destroyed.next();
    this._destroyed.complete();
  }

  /**
   * Deactivate Alert
   * @param deviceUserId
   * @returns {Obervable<boolean>}
   */
  deactivateCallLight(deviceUserId: string): Observable<boolean> {
    const apiURL = `${this.apiBaseUrl}/virtual-care/deactivate-call-light`;
    return this._http.put<boolean>(apiURL, { deviceUserId: deviceUserId });
  }

  getSessionInfo = (queryParams: { hostId: string; topic: string }) => {
    const apiUrl = `${this.apiBaseUrl}/interactive-session/session-info`;
    const urlBuilder = createUrlBuilder(apiUrl, Object.keys(queryParams));
    const urlWithQueryString = urlBuilder.build({}, queryParams);
    return this._http.get<InteractiveSessionInfo>(urlWithQueryString);
  };

  getInteractiveSession = (
    queryParams: InteractiveChannelParameters
  ): Observable<InteractiveSessionRequestResponse> => {
    const apiUrl = `${this.apiBaseUrl}/interactive-session`;
    const urlBuilder = createUrlBuilder(apiUrl, Object.keys(queryParams));
    const urlWithQueryString = urlBuilder.build({}, queryParams);
    return this._http.get<InteractiveSessionRequestResponse>(urlWithQueryString);
  };

  patchInteractiveSession = (interactiveChannelId: string, operations: Operation[]) => {
    const apiURL = `${this.apiBaseUrl}/interactive-session/${interactiveChannelId}`;
    return this._http.patch(apiURL, operations);
  };

  generateMeetingShareLink = (
    registrant: InteractiveSessionRegistrant,
    queryParams: { hostId: string; topic: string }
  ) => {
    const apiUrl = `${this.apiBaseUrl}/interactive-session/registrant`;
    const urlBuilder = createUrlBuilder(apiUrl, Object.keys(queryParams));
    const urlWithQueryString = urlBuilder.build({}, queryParams);
    return this._http.post<RegisteredUser>(urlWithQueryString, registrant);
  };

  getInteractiveSessionJoinUrl = (
    topic: string,
    hostId: string | null
  ): Observable<InteractiveSessionJoinRequestResponse> => {
    const apiURL = `${this.apiBaseUrl}/interactive-session-join/${topic}/${hostId}`;
    return this._http.get<InteractiveSessionJoinRequestResponse>(apiURL);
  };

  sendPatientMessage = (caseId: string, message: string): Observable<boolean> => {
    const apiURL = `${this.apiBaseUrl}/virtual-care/send-patient-message`;
    return this._http.post<boolean>(apiURL, {
      caseId: caseId,
      message: message
    });
  };

  sendDeviceMessage = (deviceId: string, message: string): Observable<boolean> => {
    const apiURL = `${this.apiBaseUrl}/virtual-care/send-device-message`;
    return this._http.post<boolean>(apiURL, {
      deviceId: deviceId,
      message: message
    });
  };

  acesoTvControl(tvControRequest: AcesoTvControlRequest) {
    const apiURL = `${this.apiBaseUrl}/virtual-care/aceso-tv-control`;
    return this._http.post<AcesoResponse>(apiURL, tvControRequest);
  }
}
